import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import useSettings from '../../hooks/useSettings';
import TextStack from './TextStack';

/**
 * Parse a handlebars-like template and replace variables with asset values.
 * Supports: serialNumber, location, tag, alias, and custom attribute keys.
 */
export const parseAssetTemplate = (template, asset) => {
	if (!template) return null;

	const location =
		asset.location?.name || asset.locationName || asset.location || '';

	const values = {
		serialNumber: asset.serialNumber || asset.serial || '',
		location: location,
		tag: asset.tag || '',
		alias: asset.alias || '',
	};

	if (asset.customAttributes) {
		Object.entries(asset.customAttributes).forEach(([key, value]) => {
			if (typeof value === 'string' || typeof value === 'number') {
				values[key] = value;
			}
		});
	}

	Object.entries(asset).forEach(([key, value]) => {
		if (
			!values[key] &&
			(typeof value === 'string' || typeof value === 'number')
		) {
			values[key] = value;
		}
	});

	return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, variable) => {
		const value = values[variable];
		return value !== undefined && value !== null && value !== ''
			? String(value)
			: '';
	});
};

/**
 * AssetSelectorBeta - A modern asset selector with optional grouping
 *
 * @param {Array} value - Array of selected asset IDs
 * @param {Function} onChange - Callback when selection changes (receives array of IDs)
 * @param {Array} assets - Array of asset objects
 * @param {Boolean} allowGroups - If true, shows tabs for grouping
 * @param {Array} groups - Array of group definitions: [{ key: 'manufacturer', label: 'By Manufacturer' }, { key: 'type', label: 'By Type' }]
 * @param {String} placeholder - Placeholder text when no assets selected
 * @param {Boolean} multiple - Allow multiple asset selection (default: true)
 * @param {String} className - Additional CSS classes
 */
const AssetSelectorBeta = ({
	value = [],
	onChange,
	assets = [],
	allowGroups = false,
	groups = [
		{ key: 'manufacturer', label: 'By Manufacturer' },
		{ key: 'type', label: 'By Type' },
	],
	placeholder = 'Select assets…',
	multiple = false,
	className = '',
	disablePortal = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('individual');
	const [searchQuery, setSearchQuery] = useState('');

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];

	// Clear search when closing
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('');
		}
	}, [isOpen]);

	// Filter assets based on search query
	const filteredAssets = useMemo(() => {
		if (!searchQuery.trim()) return assets;

		const query = searchQuery.toLowerCase();
		return assets.filter((asset) => {
			const searchableFields = [
				asset.name,
				asset.manufacturer,
				asset.model,
				asset.type,
				asset.location,
				asset.serial,
				asset.serialNumber,
			];

			return searchableFields.some(
				(field) => field && field.toString().toLowerCase().includes(query),
			);
		});
	}, [assets, searchQuery]);

	const handleToggle = (assetId) => {
		console.log('assetId: ', assetId);
		if (!multiple) {
			onChange(assetId);
			setIsOpen(false);
			return;
		}

		const newValue = selectedIds.includes(assetId)
			? selectedIds.filter((id) => id !== assetId)
			: [...selectedIds, assetId];
		onChange(newValue);
	};

	const handleGroupToggle = (ids) => {
		if (!multiple) return;

		const allSelected = ids.every((id) => selectedIds.includes(id));
		const newValue = allSelected
			? selectedIds.filter((id) => !ids.includes(id))
			: Array.from(new Set([...selectedIds, ...ids]));
		onChange(newValue);
	};

	const handleClear = () => {
		onChange(multiple ? [] : null);
	};

	// Get display text for trigger button
	const getDisplayText = () => {
		if (selectedIds.length === 0) {
			return (
				<>
					<span className="text-gray-400">{placeholder}</span>
				</>
			);
		}
		if (selectedIds.length === 1) {
			const asset = assets.find((a) => a.id === selectedIds[0]);
			if (!asset) return <span className="text-gray-400">{placeholder}</span>;

			const secondaryText = assetDisplayTemplate
				? parseAssetTemplate(assetDisplayTemplate, asset)
				: `${asset.location} · SN: ${asset.serial}`;

			return (
				<>
					<TextStack
						primary={asset.name}
						secondary={secondaryText}
						size="sm"
						inline
					/>
				</>
			);
		}
		return (
			<>
				<span className="text-gray-900">
					{selectedIds.length} assets selected
				</span>
			</>
		);
	};

	// Group assets by a key
	const groupAssets = (groupKey) => {
		return filteredAssets.reduce((acc, asset) => {
			const key = asset[groupKey] || 'Unknown';
			(acc[key] ||= []).push(asset);
			return acc;
		}, {});
	};

	// Render individual assets grouped by manufacturer
	const renderIndividualAssets = () => {
		const grouped = groupAssets('manufacturer');
		const hasResults = Object.keys(grouped).length > 0;

		return (
			<div className="h-[288px] overflow-y-auto shadow-inner">
				<div className="p-2">
					{!hasResults ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500">
							<p className="text-sm">No assets found</p>
							{searchQuery && (
								<p className="text-xs mt-1">Try a different search term</p>
							)}
						</div>
					) : (
						Object.entries(grouped)
							.sort(([a], [b]) => a.localeCompare(b))
							.map(([manufacturer, items]) => (
								<div key={manufacturer}>
									<p className="px-2 pt-4 mb-4 text-xxs font-semibold text-gray-400 uppercase tracking-wide">
										{manufacturer}
									</p>
									{items.map((asset) => {
										const checked = selectedIds.includes(asset.id);
										return (
											<div
												key={asset.id}
												className={`flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-gray-100 cursor-pointer ${
													checked && !multiple ? 'bg-indigo-50' : ''
												}`}
												onClick={() => handleToggle(asset.id)}
											>
												{multiple && (
													<input
														type="checkbox"
														checked={checked}
														onChange={() => handleToggle(asset.id)}
														className="mt-1 h-4 w-4 rounded-lg border-gray-300 text-indigo-600 focus:ring-indigo-500"
														onClick={(e) => e.stopPropagation()}
													/>
												)}
												<div className="flex-1 min-w-0">
													<TextStack
														primary={asset.name}
														secondary={
															assetDisplayTemplate
																? parseAssetTemplate(
																		assetDisplayTemplate,
																		asset,
																	)
																: `${asset.location} · SN: ${asset.serial}`
														}
														size="sm"
													/>
												</div>
											</div>
										);
									})}
								</div>
							))
					)}
				</div>
			</div>
		);
	};

	// Render grouped assets (by custom group key)
	const renderGroupedAssets = (groupKey) => {
		const grouped = groupAssets(groupKey);
		const hasResults = Object.keys(grouped).length > 0;

		return (
			<div className="h-[288px] overflow-y-auto shadow-inner">
				<div className="p-2 space-y-1">
					{!hasResults ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500">
							<p className="text-sm">No assets found</p>
							{searchQuery && (
								<p className="text-xs mt-1">Try a different search term</p>
							)}
						</div>
					) : (
						Object.entries(grouped)
							.sort(([a], [b]) => a.localeCompare(b))
							.map(([groupName, items]) => {
								const ids = items.map((i) => i.id);
								const allSelected = ids.every((id) => selectedIds.includes(id));
								const someSelected = ids.some((id) => selectedIds.includes(id));
								return (
									<label
										key={groupName}
										className="flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-gray-100 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={allSelected}
											ref={(input) => {
												if (input) {
													input.indeterminate = someSelected && !allSelected;
												}
											}}
											onChange={() => handleGroupToggle(ids)}
											className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
										/>
										<span className="text-sm flex-1">{groupName}</span>
										<span className="text-xs text-gray-500">
											{items.length} asset{items.length === 1 ? '' : 's'}
										</span>
									</label>
								);
							})
					)}
				</div>
			</div>
		);
	};

	return (
		<div className={className}>
			<Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[40px]"
					>
						<span className="flex items-center gap-2 truncate flex-1 min-w-0">
							{getDisplayText()}
						</span>
						<ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg ring-1 ring-black ring-opacity-5 bg-white"
					align="start"
					onClick={(e) => e.stopPropagation()}
					disablePortal={disablePortal}
				>
					{/* Search Input */}
					<div className="p-3 border-b border-gray-200 bg-white z-150">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search assets..."
								className="w-full z-150 pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none"
							/>
						</div>
					</div>

					{/* Tabs - only show if allowGroups is true */}
					{allowGroups && groups.length > 0 && (
						<div className="grid grid-cols-2 border-b bg-gray-50">
							<button
								type="button"
								onClick={() => setActiveTab('individual')}
								className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
									activeTab === 'individual'
										? 'border-indigo-600 text-indigo-600 bg-white'
										: 'border-transparent text-gray-500 hover:text-gray-700'
								}`}
							>
								Individual
							</button>
							<button
								type="button"
								onClick={() => setActiveTab('groups')}
								className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
									activeTab === 'groups'
										? 'border-indigo-600 text-indigo-600 bg-white'
										: 'border-transparent text-gray-500 hover:text-gray-700'
								}`}
							>
								{groups[0]?.label || 'By Group'}
							</button>
						</div>
					)}

					{/* Content */}
					{!allowGroups || activeTab === 'individual'
						? renderIndividualAssets()
						: renderGroupedAssets(groups[0]?.key || 'type')}

					{/* Footer */}
					{multiple && selectedIds.length > 0 && (
						<div className="flex items-center justify-between border-t px-3 py-2 bg-gray-50">
							<span className="text-xs text-gray-500">
								{selectedIds.length} selected
							</span>
							<button
								type="button"
								onClick={handleClear}
								className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
							>
								Clear
							</button>
						</div>
					)}
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default AssetSelectorBeta;
